import { MockCard } from '../../components/mockups/MockUI'
import { MockStatusBadge, MockPriorityBadge } from '../../components/mockups/MockBadge'
import { MockTable } from '../../components/mockups/MockTable'

export function RequestsStatuses() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Статусы заявок
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Заявка проходит цепочку статусов от создания до закрытия. Почти все переходы выставляются
          системой автоматически — вручную менеджер задаёт только исход сделки.
        </p>
      </div>

      <MockTable
        caption="Статусы заявки и их значение"
        columns={[{ header: 'Статус' }, { header: 'Описание' }, { header: 'Как попадает' }]}
        rows={[
          [<MockStatusBadge status="NEW" />, 'Только создана', 'Автоматически при создании'],
          [
            <MockStatusBadge status="SENT_TO_PROCUREMENT" />,
            'Отправлена закупщикам',
            'Кнопка «Отправить в закупку»',
          ],
          [
            <MockStatusBadge status="IN_PROGRESS" />,
            'Идёт работа — есть квоты с ценой',
            'Автоматически при первой квоте с ценой > 0',
          ],
          [
            <MockStatusBadge status="QUOTATION_READY" />,
            'Готово к КП — все нужные позиции отработаны',
            'Подписывает РОП или РОЗ («готово к КП»)',
          ],
          [
            <MockStatusBadge status="QUOTATION_CREATED" />,
            'КП сформировано (Excel + снапшот сохранены)',
            'Автоматически после «Скачать КП»',
          ],
          [
            <MockStatusBadge status="QUOTE_SENT" />,
            'КП отправлено клиенту',
            'Кнопка «КП отправлено»',
          ],
          [
            <MockStatusBadge status="CLOSED_WON" />,
            'Счёт — клиент согласовал КП, выставлен счёт',
            'Кнопка «Выставить счёт»',
          ],
          [
            <MockStatusBadge status="CLOSED_LOST" />,
            'Клиент отказался',
            'Выпадашка статуса → «Отказ»',
          ],
          [
            <MockStatusBadge status="ARCHIVED" />,
            'В архиве — заявка убрана из работы',
            'Выпадашка статуса → «В архив»',
          ],
        ]}
      />

      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Ручная смена статуса заявки:
          </div>
          <p>
            В выпадающем списке статуса заявки доступны только два ручных исхода —{' '}
            <span className="font-medium">«Отказ»</span> и{' '}
            <span className="font-medium">«В архив»</span>. Их можно выбрать из любого рабочего
            статуса. Остальные статусы система выставляет сама по ходу работы, поэтому поля
            произвольного («своего») статуса больше нет.
          </p>
        </div>
      </MockCard>

      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Автоматические переходы:
          </div>
          <ul className="list-disc list-inside space-y-1">
            <li>Появилась первая квота с реальной ценой → заявка переходит в «В работе»</li>
            <li>Квоты с ценой 0 («заготовки») статус не двигают — нужна цена больше нуля</li>
            <li>
              Нажатие «Скачать КП» → заявка переходит в «КП создано»; позиции, попавшие в КП, тоже
              «КП создано», не вошедшие — остаются в исходном статусе
            </li>
            <li>
              Позиция со статусом «Не найдено» считается отработанной — она не мешает заявке стать
              «Готова к КП»
            </li>
          </ul>
        </div>
      </MockCard>
    </div>
  )
}

export function RequestsItems() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Позиции заявки
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Каждая заявка содержит список позиций — конкретных компонентов, которые нужны клиенту.
        </p>
      </div>

      <MockTable
        caption="Статусы позиций"
        columns={[{ header: 'Статус' }, { header: 'Описание' }]}
        rows={[
          [<MockStatusBadge status="NEW" />, 'Только создана, ещё не взята в работу'],
          [
            <MockStatusBadge status="IN_QUEUE" />,
            'Закупщик сформировал запрос — позиция в очереди',
          ],
          [<MockStatusBadge status="FOUND" />, 'Появилась квота с реальной ценой (цена найдена)'],
          [
            <MockStatusBadge status="NOT_FOUND" />,
            'Все отправленные поставщикам запросы отвечены, и все — ценой 0. Реальной цены нет. Статус выставляется автоматически; новый запрос поставщику вернёт позицию «В очередь»',
          ],
          [<MockStatusBadge status="NO_RESPONSE" />, 'Поставщик не ответил'],
          [<MockPriorityBadge priority="HIGH" />, 'Высокий приоритет — обрабатывать первым'],
          [<MockPriorityBadge priority="MEDIUM" />, 'Средний приоритет (по умолчанию)'],
          [<MockPriorityBadge priority="LOW" />, 'Низкий приоритет'],
        ]}
      />

      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Редактирование позиций:
          </div>
          <ul className="list-disc list-inside space-y-1">
            <li>Партномер, бренд, заметка — редактируются кликом прямо в ячейке таблицы</li>
            <li>Приоритет — через выпадающий список в ячейке</li>
            <li>Матрица (Склад/Китай/РФ/Производители/Стоки) — определяет источник закупки</li>
            <li>Назначение закупщика — dropdown в колонке «Назначена»</li>
            <li>
              Статус позиции вручную не выставляется — он вычисляется автоматически по квотам и
              ответам поставщиков
            </li>
          </ul>
        </div>
      </MockCard>
    </div>
  )
}

export function RequestsAssign() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Назначение закупщика
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Менеджер, РОП или ADMIN может назначить закупщика на конкретные позиции заявки.
        </p>
      </div>

      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">Способы назначения:</div>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-brand-600 font-medium">1.</span>
              <div>
                <span className="font-medium">Одиночное</span> — кликнуть на ячейку «Назначена» в
                строке позиции → выбрать закупщика из списка
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-brand-600 font-medium">2.</span>
              <div>
                <span className="font-medium">Массовое</span> — выбрать чекбоксами несколько позиций
                → нажать «Назначить» в панели действий → выбрать закупщика
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-brand-600 font-medium">3.</span>
              <div>
                <span className="font-medium">Автоматическое</span> — при выборе матрицы «Склад»,
                «РФ» или «Стоки» позиция автоматически назначается на текущего менеджера
              </div>
            </div>
          </div>
        </div>
      </MockCard>
    </div>
  )
}
